import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useTranslation } from "react-i18next";
import { fetchStats } from "@/lib/api";
import { devLog } from "@/lib/devLog";

const formatCount = (n) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1).replace(/\.0$/, "")}K`;
  return `${n}`;
};

const Counter = ({ end, suffix = "", prefix = "", duration = 1400, format = formatCount }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  // During the react-snap headless prerender pass the user-agent is
  // literally "ReactSnap" (configured in package.json). In that pass we
  // skip the count-up animation entirely and render the final value —
  // otherwise the static HTML would snapshot the intermediate 0 state
  // and ship "0+ enterprises served" to non-JS crawlers.
  const isPrerender =
    typeof navigator !== "undefined" && /ReactSnap/i.test(navigator.userAgent || "");
  const [val, setVal] = useState(isPrerender ? end : 0);

  useEffect(() => {
    if (isPrerender) return;
    if (!inView) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setVal(end); return; }
    let start = 0;
    const t0 = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(start + (end - start) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [inView, end, duration, isPrerender]);

  return <span ref={ref} className="tabular-nums">{prefix}{format(val)}{suffix}</span>;
};

export const StatsCounter = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    enterprises_served: 223,
    markets_covered: 28,
    monthly_ad_spend_usd: 10_000_000,
    // Response window is expressed as a range (lower–upper hours), so it
    // ships as a string and skips the count-up animation via `static:true`
    // below. Kept in sync with the "2–3 hour" SLA copy across the site.
    response_window_hours: "2–3",
  });

  useEffect(() => {
    fetchStats()
      .then((d) => setStats((s) => ({ ...s, ...d })))
      .catch((err) => {
        // Stats endpoint failure is non-fatal — the component keeps the
        // sensible default numbers rendered on first paint.
        devLog.warn("StatsCounter: fetchStats failed, using defaults", err);
      });
  }, []);

  const items = [
    { label: t("stats.enterprises"), value: stats.enterprises_served, suffix: "+" },
    { label: t("stats.markets"), value: stats.markets_covered, suffix: "+" },
    { label: t("stats.adSpend"), value: stats.monthly_ad_spend_usd, prefix: "$", suffix: "+" },
    // `static: true` → render the range verbatim without the count-up animation.
    { label: t("stats.response"), value: stats.response_window_hours, suffix: "h", static: true },
  ];

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4 }}
      className="mx-auto mt-10 w-full max-w-6xl px-4 sm:px-6 lg:px-8"
      data-testid="animated-stats-section"
    >
      <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((it) => (
          <div key={it.label} className="px-2">
            <div className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              {it.static ? (
                <span className="tabular-nums">{it.prefix || ""}{it.value}{it.suffix}</span>
              ) : (
                <Counter end={it.value} prefix={it.prefix || ""} suffix={it.suffix} format={it.format} />
              )}
            </div>
            <div className="mt-1 text-sm text-slate-500">{it.label}</div>
          </div>
        ))}
      </div>
    </motion.section>
  );
};
